// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { BagIcon, Typography, useToast } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { cartSize } from '@jane/shared-ecomm/util';
import type { Cart, EmptyCart } from '@jane/shared/types';

import { useKioskSpacing } from '../../../hooks';
import { IconButton } from '../IconButton/IconButton';
import { ProductAddedToast } from '../ProductAddedToast/ProductAddedToast';

export interface CartButtonProps {
  cart?: Nullable<Cart | EmptyCart>;
  onClick: () => void;
  storeId: number;
}

const Badge = styled.div(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.grays.black,
  borderRadius: '50%',
  color: theme.colors.text.info,
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  position: 'absolute' as const,
  right: 0,
  top: 0,
}));

const badgeStyle: CSSProperties = {
  fontSize: 24,
  height: 32,
  width: 32,
};

const tabletBadgeStyle: CSSProperties = {
  fontSize: 16,
  height: 24,
  width: 24,
};

export const CartButton = ({ cart, onClick, storeId }: CartButtonProps) => {
  const { pathname } = useLocation();
  const toast = useToast();
  const { isIpad } = useKioskSpacing();

  const productCount = cartSize(cart);

  const countRef = useRef(productCount);

  useEffect(() => {
    if (productCount !== countRef.current) {
      const diff = productCount - countRef.current;

      if (diff > 0) {
        toast.add({
          label: (
            <ProductAddedToast
              productCount={diff}
              showCartLink={!pathname.includes('cart')}
              storeId={storeId}
            />
          ),
        });
      }

      countRef.current = productCount;
    }
  }, [pathname, productCount, storeId, toast]);

  return (
    <IconButton
      ariaLabel="products bag"
      disabled={productCount === 0}
      onClick={onClick}
      icon={<BagIcon color="grays-black" size={isIpad ? 'lg' : 'xl'} />}
    >
      {productCount > 0 && (
        <Badge
          aria-label="cart size"
          style={isIpad ? tabletBadgeStyle : badgeStyle}
        >
          <Typography color="grays-white">{productCount}</Typography>
        </Badge>
      )}
    </IconButton>
  );
};
